import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Upload, X } from "lucide-react";
import Logo from "../components/Logo";

const STEPS = [
  "type", "contact", "vision", "style", "logistics", "budget", "notes", "files", "done",
];

const STEP_LABEL = {
  type: "Project Type", contact: "Contact", vision: "Vision", style: "Style",
  logistics: "Logistics", budget: "Budget", notes: "Notes", files: "References & Submit",
  done: "Confirmed",
};

const PROJECT_TYPES = ["Wedding", "Commercial", "Music Video", "Documentary", "Event", "Brand Film", "Other"];
const VIDEO_STYLES = ["Cinematic", "Documentary", "Editorial", "Minimalist", "High-Energy", "Moody", "Lifestyle", "Not sure"];
const BUDGETS = ["Under $2k", "$2k – $5k", "$5k – $10k", "$10k – $25k", "$25k+", "Not sure yet"];

const LAST_INPUT = STEPS.indexOf("files"); // 7
const DONE = STEPS.indexOf("done");        // 8

const Inquire = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const fileInputRef = useRef(null);
  const [data, setData] = useState({
    project_type: "", client_name: "", client_email: "", client_phone: "",
    vision: "", video_style: "", shoot_date: "", location: "",
    budget: "", deadline: "", notes: "",
  });
  const [files, setFiles] = useState([]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    setFiles((prev) => [...prev, ...arr]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v != null && v !== "") fd.append(k, v); });
      fd.append("scheduled", "false");
      files.forEach((f) => fd.append("files", f));
      const { data: resp } = await api.post("/inquiry", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmittedId(resp.project_id);
      setStep(DONE);
      toast.success("Inquiry submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return !!data.project_type;
    if (step === 1) return data.client_name && data.client_email && data.client_phone;
    return true;
  };

  return (
    <div className="min-h-screen relative">
      <div
        className="fixed inset-0 opacity-20"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/34518947/pexels-photo-34518947.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024&w=1536')`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#050505]" />

      <div className="relative max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-12">
          <Logo size={40} />
          <button onClick={() => nav("/admin/login")} className="label-caps hover:text-white transition" data-testid="admin-access-link">
            Admin →
          </button>
        </div>

        {/* Progress */}
        {step < DONE && (
          <div className="mb-12">
            <div className="flex justify-between mb-3">
              <span className="label-caps">Step {step + 1} / {DONE}</span>
              <span className="label-caps">{STEP_LABEL[STEPS[step]]}</span>
            </div>
            <div className="h-px bg-white/10 relative">
              <div
                className="absolute inset-y-0 left-0 bg-[#F4F4F0] transition-all duration-500"
                style={{ width: `${((step + 1) / DONE) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="min-h-[420px]" data-testid={`wizard-step-${STEPS[step]}`}>
          {step === 0 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">What are we making?</h1>
              <p className="text-white/50 mb-10">Choose the type of project you have in mind.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/5">
                {PROJECT_TYPES.map((t) => (
                  <button key={t} data-testid={`wizard-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => set("project_type", t)}
                    className={`p-8 text-left transition-all ${data.project_type === t ? "bg-[#F4F4F0] text-black" : "bg-[#0A0A0A] hover:bg-[#121212]"}`}>
                    <div className="font-editorial text-2xl">{t}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">Who are you?</h1>
              <p className="text-white/50 mb-10">We'll use this to stay in touch.</p>
              <div className="space-y-5">
                <div>
                  <label className="label-caps mb-2 block">Full Name</label>
                  <input data-testid="wizard-name" className="input-editorial" value={data.client_name} onChange={(e) => set("client_name", e.target.value)} />
                </div>
                <div>
                  <label className="label-caps mb-2 block">Email</label>
                  <input data-testid="wizard-email" type="email" className="input-editorial" value={data.client_email} onChange={(e) => set("client_email", e.target.value)} />
                </div>
                <div>
                  <label className="label-caps mb-2 block">Phone</label>
                  <input data-testid="wizard-phone" className="input-editorial" value={data.client_phone} onChange={(e) => set("client_phone", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">The vision.</h1>
              <p className="text-white/50 mb-10">Tell us what this project means to you.</p>
              <textarea data-testid="wizard-vision" rows={8} className="input-editorial font-editorial text-xl" placeholder="A short film about..." value={data.vision} onChange={(e) => set("vision", e.target.value)} />
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">Desired style.</h1>
              <p className="text-white/50 mb-10">How should it feel on screen?</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
                {VIDEO_STYLES.map((v) => (
                  <button key={v} data-testid={`wizard-style-${v.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => set("video_style", v)}
                    className={`p-6 text-left transition-all ${data.video_style === v ? "bg-[#F4F4F0] text-black" : "bg-[#0A0A0A] hover:bg-[#121212]"}`}>
                    <div className="font-editorial text-lg">{v}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">Logistics.</h1>
              <p className="text-white/50 mb-10">When and where?</p>
              <div className="space-y-5">
                <div>
                  <label className="label-caps mb-2 block">Preferred Filming Date(s)</label>
                  <input data-testid="wizard-date" type="date" className="input-editorial" value={data.shoot_date} onChange={(e) => set("shoot_date", e.target.value)} />
                </div>
                <div>
                  <label className="label-caps mb-2 block">Location</label>
                  <input data-testid="wizard-location" className="input-editorial" placeholder="City or venue" value={data.location} onChange={(e) => set("location", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">Budget & deadline.</h1>
              <p className="text-white/50 mb-10">Rough figures help us scope.</p>
              <div className="mb-8">
                <div className="label-caps mb-3">Budget Range</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/5">
                  {BUDGETS.map((b) => (
                    <button key={b} data-testid={`wizard-budget-${b.replace(/[^a-z0-9]/gi, "")}`}
                      onClick={() => set("budget", b)}
                      className={`p-6 text-left transition-all ${data.budget === b ? "bg-[#F4F4F0] text-black" : "bg-[#0A0A0A] hover:bg-[#121212]"}`}>
                      <div className="font-editorial text-lg">{b}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-caps mb-2 block">Desired Delivery Date</label>
                <input data-testid="wizard-deadline" type="date" className="input-editorial" value={data.deadline} onChange={(e) => set("deadline", e.target.value)} />
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">Anything else?</h1>
              <p className="text-white/50 mb-10">Special requests, notes, accessibility needs.</p>
              <textarea data-testid="wizard-notes" rows={8} className="input-editorial" placeholder="We need a 60-second cut for Instagram, captions required..." value={data.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>
          )}

          {step === 7 && (
            <div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tight leading-none mb-4">References & assets.</h1>
              <p className="text-white/50 mb-10">Upload inspiration, logos, brand assets, moodboards — anything that helps us see it. Optional.</p>
              <div
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                onClick={() => fileInputRef.current?.click()}
                className="border border-dashed border-white/20 p-16 text-center cursor-pointer hover:border-white/40 hover:bg-white/[0.02] transition"
                data-testid="wizard-dropzone"
              >
                <Upload size={24} className="mx-auto mb-4 text-white/40" />
                <div className="font-editorial text-2xl mb-1">Drop files or click to browse</div>
                <div className="label-caps text-white/30">Images, PDFs, videos — anything</div>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="wizard-file-input"/>
              </div>
              {files.length > 0 && (
                <div className="mt-6 space-y-px bg-white/5">
                  {files.map((f, i) => (
                    <div key={i} className="bg-[#0A0A0A] p-4 flex items-center justify-between" data-testid={`wizard-file-${i}`}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{f.name}</div>
                        <div className="font-mono-alt text-xs text-white/40">{(f.size / 1024).toFixed(1)} KB</div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-white/40 hover:text-red-400 ml-4"><X size={16}/></button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-white/40 mt-8">After you submit, we'll be in touch within 24 hours to schedule your consultation.</p>
            </div>
          )}

          {step === DONE && (
            <div className="text-center py-16 animate-fade-up">
              <div className="w-16 h-16 border border-white/30 mx-auto mb-8 flex items-center justify-center">
                <Check size={24} />
              </div>
              <h1 className="font-editorial text-6xl tracking-tight leading-none mb-4">Received.</h1>
              <p className="text-white/60 mb-4">Thank you, {data.client_name}.</p>
              <p className="text-white/50 mb-12 max-w-md mx-auto">
                We'll be in touch within 24 hours to follow up and schedule your consultation.
              </p>
              <div className="label-caps text-white/30 mb-2">Reference ID</div>
              <div className="font-mono-alt text-xs text-white/50 mb-12" data-testid="confirmation-ref">{submittedId?.slice(0, 8)}</div>
              <button onClick={() => window.location.reload()} className="btn-ghost" data-testid="wizard-done">Submit Another</button>
            </div>
          )}
        </div>

        {/* Navigation */}
        {step < DONE && (
          <div className="flex justify-between mt-12 pt-8 border-t border-white/10">
            <button onClick={back} disabled={step === 0} data-testid="wizard-prev" className="btn-ghost inline-flex items-center gap-2 disabled:opacity-30">
              <ArrowLeft size={14} /> Back
            </button>
            {step === LAST_INPUT ? (
              <button onClick={submit} disabled={loading} data-testid="wizard-submit" className="btn-primary inline-flex items-center gap-2">
                {loading ? "Submitting..." : <>Submit Inquiry <ArrowRight size={14} /></>}
              </button>
            ) : (
              <button onClick={next} disabled={!canProceed()} data-testid="wizard-next" className="btn-primary inline-flex items-center gap-2 disabled:opacity-30">
                Continue <ArrowRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Inquire;
